/**
 * Seeded, deterministic pseudo-random number generation.
 *
 * This module is the ONLY sanctioned source of randomness in the project.
 * `Math.random` is banned by lint (see eslint.config.js) precisely so that
 * every generated value flows through here and is therefore reproducible
 * from a seed.
 *
 * Algorithm: sfc32 (Small Fast Counter, 32-bit), seeded by splitmix32.
 * sfc32 has a 128-bit state, passes PractRand, and is fast in JS because it
 * uses only 32-bit integer ops. It is NOT cryptographically secure and must
 * never be used for secrets or tokens.
 */

/** The 128-bit generator state, as four uint32 words. */
export type RngState = readonly [number, number, number, number];

/** Anything that can be turned into a seed. */
export type Seedable = string | number;

const TWO_POW_32 = 0x1_0000_0000;
/** 1 / 2^32, used to map a uint32 into [0, 1). */
const INV_TWO_POW_32 = 1 / TWO_POW_32;

/**
 * splitmix32 finalizer. Avalanches a 32-bit integer so that seeds which
 * differ by one bit produce completely unrelated states.
 */
function splitmix32(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x9e37_79b9) >>> 0;
    let z = state;
    z = Math.imul(z ^ (z >>> 16), 0x21f0_aaad);
    z = Math.imul(z ^ (z >>> 15), 0x735a_2d97);
    return (z ^ (z >>> 15)) >>> 0;
  };
}

/**
 * Hash an arbitrary seed value into a uint32.
 *
 * Strings are hashed with FNV-1a over their UTF-16 code units, so
 * `hashSeed('cavern-of-echoes')` is stable across runs, platforms and
 * releases. Numbers are avalanched so that `1` and `2` are unrelated.
 */
export function hashSeed(seed: Seedable): number {
  if (typeof seed === 'number') {
    if (!Number.isFinite(seed)) {
      throw new RangeError(`Seed must be a finite number, received ${seed}`);
    }
    // Fold the whole double (including the fractional part) into 32 bits.
    const buffer = new ArrayBuffer(8);
    new Float64Array(buffer)[0] = seed;
    const words = new Uint32Array(buffer);
    const low = words[0] as number;
    const high = words[1] as number;
    return splitmix32((low ^ Math.imul(high, 0x85eb_ca6b)) >>> 0)();
  }

  let hash = 0x811c_9dc5;
  for (let i = 0; i < seed.length; i += 1) {
    hash ^= seed.charCodeAt(i);
    hash = Math.imul(hash, 0x0100_0193);
  }
  // Mix in the length so that '' and '\0' differ.
  hash ^= seed.length;
  return splitmix32(hash >>> 0)();
}

/** Expand a single uint32 seed into a full 128-bit sfc32 state. */
function expandSeed(seed: number): RngState {
  const next = splitmix32(seed);
  return [next(), next(), next(), next()];
}

/**
 * A deterministic random number generator.
 *
 * Two instances constructed with the same seed always produce byte-identical
 * sequences, forever. Treat the sequence as part of the save format: changing
 * the algorithm or the order of draws changes every generated world.
 */
export class Rng {
  #a: number;
  #b: number;
  #c: number;
  #d: number;

  /** The seed this generator was created from, for logging and bug reports. */
  readonly seed: Seedable;

  constructor(seed: Seedable = 0) {
    this.seed = seed;
    const [a, b, c, d] = expandSeed(hashSeed(seed));
    this.#a = a;
    this.#b = b;
    this.#c = c;
    this.#d = d;
    // Discard the first draws so that low-entropy seeds are well mixed.
    for (let i = 0; i < 20; i += 1) this.nextUint32();
  }

  /** Rebuild a generator from a previously captured state. */
  static fromState(state: RngState, seed: Seedable = 0): Rng {
    const rng = new Rng(seed);
    rng.setState(state);
    return rng;
  }

  /**
   * Capture the exact generator state, for saving or resuming.
   * Always returns unsigned words, so the state serializes identically no
   * matter how it was reached.
   */
  getState(): RngState {
    return [this.#a >>> 0, this.#b >>> 0, this.#c >>> 0, this.#d >>> 0];
  }

  /** Restore a previously captured state. */
  setState(state: RngState): void {
    const [a, b, c, d] = state;
    this.#a = a >>> 0;
    this.#b = b >>> 0;
    this.#c = c >>> 0;
    this.#d = d >>> 0;
  }

  /** An independent copy positioned at the current state. */
  clone(): Rng {
    return Rng.fromState(this.getState(), this.seed);
  }

  /**
   * The raw generator step: a uniform integer in [0, 2^32).
   * Every other method on this class is built from this one.
   */
  nextUint32(): number {
    const t = (((this.#a + this.#b) | 0) + this.#d) | 0;
    this.#d = (this.#d + 1) | 0;
    this.#a = this.#b ^ (this.#b >>> 9);
    this.#b = (this.#c + (this.#c << 3)) | 0;
    this.#c = (this.#c << 21) | (this.#c >>> 11);
    this.#c = (this.#c + t) | 0;
    return t >>> 0;
  }

  /** A uniform float in [0, 1). The direct replacement for `Math.random()`. */
  float(): number {
    return this.nextUint32() * INV_TWO_POW_32;
  }

  /** A uniform float in [min, max). */
  range(min: number, max: number): number {
    return min + this.float() * (max - min);
  }

  /**
   * A uniform integer in [minInclusive, maxExclusive).
   * Uses rejection sampling, so the result is unbiased for every range.
   */
  int(minInclusive: number, maxExclusive: number): number {
    const min = Math.ceil(minInclusive);
    const max = Math.ceil(maxExclusive);
    const span = max - min;
    if (span <= 0) {
      throw new RangeError(
        `Empty integer range: [${minInclusive}, ${maxExclusive})`,
      );
    }
    if (span > TWO_POW_32) {
      throw new RangeError(`Integer range wider than 2^32: ${span}`);
    }
    // Reject the ragged tail of [0, 2^32) so every value is equally likely.
    const limit = TWO_POW_32 - (TWO_POW_32 % span);
    let draw = this.nextUint32();
    while (draw >= limit) draw = this.nextUint32();
    return min + (draw % span);
  }

  /** A uniform integer in [min, max], both ends included. */
  intInclusive(min: number, max: number): number {
    return this.int(min, max + 1);
  }

  /** True with probability `p` (default one half). */
  bool(p = 0.5): boolean {
    return this.float() < p;
  }

  /** A uniformly chosen element. Throws on an empty collection. */
  pick<T>(items: readonly T[]): T {
    if (items.length === 0) {
      throw new RangeError('Cannot pick from an empty array');
    }
    return items[this.int(0, items.length)] as T;
  }

  /**
   * Choose an element with probability proportional to its weight.
   * Weights must be finite and non-negative, and at least one must be > 0.
   */
  weighted<T>(entries: readonly (readonly [T, number])[]): T {
    let total = 0;
    for (const [, weight] of entries) {
      if (!Number.isFinite(weight) || weight < 0) {
        throw new RangeError(`Weights must be finite and >= 0, got ${weight}`);
      }
      total += weight;
    }
    if (total <= 0) {
      throw new RangeError('Total weight must be greater than zero');
    }
    let roll = this.float() * total;
    for (const [value, weight] of entries) {
      roll -= weight;
      if (roll < 0) return value;
    }
    // Only reachable through floating point drift on the final entry.
    return entries[entries.length - 1]![0];
  }

  /** A shuffled copy, via Fisher-Yates. The input is not modified. */
  shuffle<T>(items: readonly T[]): T[] {
    const out = items.slice();
    for (let i = out.length - 1; i > 0; i -= 1) {
      const j = this.int(0, i + 1);
      [out[i], out[j]] = [out[j] as T, out[i] as T];
    }
    return out;
  }

  /** `count` distinct elements, in random order, without replacement. */
  sample<T>(items: readonly T[], count: number): T[] {
    if (count < 0 || count > items.length) {
      throw new RangeError(
        `Cannot sample ${count} of ${items.length} items`,
      );
    }
    return this.shuffle(items).slice(0, count);
  }

  /**
   * Derive an independent named sub-generator.
   *
   * Forking does NOT advance this generator, so streams are stable no matter
   * when or in what order they are created: `world.fork('terrain')` yields the
   * same sequence whether it happens before or after `world.fork('loot')`.
   * Give each subsystem its own fork and adding a draw in one subsystem will
   * not reshuffle the others.
   */
  fork(label: string): Rng {
    const [a, b, c, d] = this.getState();
    const salt = hashSeed(label);
    const mixed = splitmix32(
      (a ^ Math.imul(b, 0x27d4_eb2f) ^ Math.imul(c, 0x1656_67b1) ^ d ^ salt) >>>
        0,
    )();
    const child = new Rng(0);
    child.setState(expandSeed(mixed));
    for (let i = 0; i < 20; i += 1) child.nextUint32();
    return child;
  }
}

/**
 * Produce a fresh, unpredictable seed.
 *
 * This is the single intentional point of non-determinism in `/sim`: it exists
 * to CHOOSE a seed, never to generate content. Everything downstream must go
 * through an `Rng` built from the value this returns, so any session can be
 * reproduced exactly by feeding the same seed back in.
 */
export function createSeed(): number {
  const webCrypto = globalThis.crypto;
  if (!webCrypto?.getRandomValues) {
    throw new Error(
      'WebCrypto is unavailable, so no seed can be generated. Pass an explicit ' +
        'seed instead — /sim must never fall back to unseeded randomness.',
    );
  }
  return webCrypto.getRandomValues(new Uint32Array(1))[0] as number;
}
